import { del, get, post, put } from '../lib/apiClient';

/** Community marketplace (docs/Decisions.md D68). */

// Categories
export const listCategories = query =>
  get('/marketplace/categories', query ? { query } : undefined);
export const createCategory = data => post('/marketplace/categories', data);
export const updateCategory = (categoryId, data) =>
  put(`/marketplace/categories/${encodeURIComponent(categoryId)}`, data);
export const deleteCategory = categoryId =>
  del(`/marketplace/categories/${encodeURIComponent(categoryId)}`);
export const importCategories = categoryIds =>
  post('/marketplace/categories/import', { categoryIds });

// Vendor profiles
export const listMyVendors = () => get('/marketplace/vendors/mine');
export const createVendor = data => post('/marketplace/vendors', data);
export const updateVendor = (vendorId, data) =>
  put(`/marketplace/vendors/${encodeURIComponent(vendorId)}`, data);
export const getVendor = vendorId => get(`/marketplace/vendors/${encodeURIComponent(vendorId)}`);
export const listDiscoverableVendors = () =>
  get('/marketplace/vendors', { query: { discoverable: 'true' } });
export const setVendorStatus = (vendorId, status, note) =>
  put(`/marketplace/vendors/${encodeURIComponent(vendorId)}/status`, { status, note });

// Listings
export const listOrgListings = status =>
  get('/marketplace/listings', { query: { scope: 'org', ...(status ? { status } : {}) } });
export const listVendorListings = vendorId =>
  get('/marketplace/listings', { query: { scope: 'vendor', vendorId } });
export const getListing = listingId =>
  get(`/marketplace/listings/${encodeURIComponent(listingId)}`);
export const applyToList = (vendorId, categoryIds, requirements) =>
  post('/marketplace/listings', { vendorId, categoryIds, requirements, acceptTerms: true });
export const actOnListing = (listingId, action, note, checks) =>
  post(`/marketplace/listings/${encodeURIComponent(listingId)}/actions`, {
    action,
    note,
    checks,
  });
export const inviteVendor = (vendorId, note) =>
  post('/marketplace/invitations', { vendorId, note });

// Directory
export const getDirectory = query => get('/marketplace/directory', { query });

// Interest lists and the sales tracker (M2)
export const joinList = (vendorId, { serviceName, categoryId, note, phone, ref }) =>
  post(`/marketplace/vendors/${encodeURIComponent(vendorId)}/leads`, {
    serviceName,
    categoryId,
    note,
    phone,
    ref,
    consent: true,
  });
export const listMyLeads = () => get('/marketplace/leads', { query: { scope: 'member' } });
export const listVendorLeads = vendorId =>
  get('/marketplace/leads', { query: { scope: 'vendor', vendorId } });
export const listOrgLeads = () => get('/marketplace/leads', { query: { scope: 'org' } });
export const actOnLead = (leadId, action, extra = {}) =>
  post(`/marketplace/leads/${encodeURIComponent(leadId)}/actions`, {
    action,
    note: extra.note,
    dealValueCents: extra.dealValueCents,
    reason: extra.reason,
    salespersonId: extra.salespersonId,
  });
export const setSalespeople = (listingId, salespeople) =>
  put(`/marketplace/listings/${encodeURIComponent(listingId)}/salespeople`, { salespeople });

// Follow-ups, reviews, recommendations and the quality queue (M3)
export const answerFollowUp = (leadId, { purchased, pricePaidCents, review, recommend }) =>
  post(`/marketplace/leads/${encodeURIComponent(leadId)}/follow-up`, {
    purchased,
    pricePaidCents,
    review,
    recommend,
  });
export const listReviews = vendorId =>
  get(`/marketplace/vendors/${encodeURIComponent(vendorId)}/reviews`);
export const respondToReview = (reviewId, text) =>
  post(`/marketplace/reviews/${encodeURIComponent(reviewId)}/response`, { text });
export const moderateReview = (reviewId, action, reason, note) =>
  post(`/marketplace/reviews/${encodeURIComponent(reviewId)}/moderation`, { action, reason, note });
export const listRecommendations = userId =>
  get('/marketplace/recommendations', userId ? { query: { userId } } : undefined);
export const recommendVendor = vendorId =>
  put(`/marketplace/recommendations/${encodeURIComponent(vendorId)}`);
export const unrecommendVendor = vendorId =>
  del(`/marketplace/recommendations/${encodeURIComponent(vendorId)}`);
export const resolveMismatch = (leadId, note) =>
  post(`/marketplace/leads/${encodeURIComponent(leadId)}/mismatch-resolution`, { note });

// Fees, vendor billing and payouts (M4)
export const getVendorBilling = vendorId =>
  get(`/marketplace/vendors/${encodeURIComponent(vendorId)}/billing`);
export const startVendorBillingSetup = (vendorId, successUrl, cancelUrl) =>
  post(`/marketplace/vendors/${encodeURIComponent(vendorId)}/billing/setup`, {
    successUrl,
    cancelUrl,
  });
export const listOrgFees = () => get('/marketplace/fees', { query: { scope: 'org' } });
export const listPlatformFees = status =>
  get('/marketplace/fees', { query: { scope: 'platform', status } });
export const creditFee = (entryId, note) =>
  post(`/marketplace/fees/${encodeURIComponent(entryId)}/credit`, { note });
export const getPayoutAccount = kind =>
  get('/marketplace/payout-account', { query: { for: kind } });
export const startPayoutOnboarding = (kind, returnUrl, refreshUrl) =>
  post('/marketplace/payout-account/onboarding', { for: kind, returnUrl, refreshUrl });

// Recommender reward programs (M5)
export const setRewardProgram = (listingId, { type, percent, amountCents, credits, terms }) =>
  put(`/marketplace/listings/${encodeURIComponent(listingId)}/reward-program`, {
    type,
    percent,
    amountCents,
    credits,
    terms,
  });
export const listMyRewards = () => get('/marketplace/rewards');

// Vendor logo uploads through the scanner (FEA-49, D74)
export const requestVendorLogoUpload = (vendorId, file) =>
  post(`/marketplace/vendors/${encodeURIComponent(vendorId)}/logo`, {
    contentType: file.type,
    size: file.size,
  });
export const confirmVendorLogo = (vendorId, logoId) =>
  post(`/marketplace/vendors/${encodeURIComponent(vendorId)}/logo/confirm`, { logoId });
export const deleteVendorLogo = vendorId =>
  del(`/marketplace/vendors/${encodeURIComponent(vendorId)}/logo`);
